import React from 'react';

import {
    SelectTextField,
    type SingleSelectTextFieldProps,
} from '@lumapps/combobox/components/SelectTextField/SelectTextField';

import { FILTERS_MODE } from '../../constants';
import { useFilterUIProps } from '../../hooks/useFilterUIProps';
import {
    UseSingleSelectFilter,
    useSingleSelectFilter,
    UseSingleSelectFilterOptions,
} from '../../hooks/useSingleSelectFilter';
import { Filter, type FilterProps } from '../Filter';

export interface SingleSelectFilterProps<T> extends FilterProps {
    /** list of options to display as list items */
    options: T[];
    /** onSelected callback, generated from the `useListFilter` hook */
    onSelected?: UseSingleSelectFilter<T>['onSelected'];
    /** onFilter callback, generated from the `useListFilter` hook */
    onFilter?: UseSingleSelectFilter<T>['onFilter'];
    /** onClearSelected callback, generated from the `useListFilter` hook */
    onClearSelected?: UseSingleSelectFilter<T>['onClearSelected'];
    /** isInSelection callback, generated from the `useListFilter` hook */
    selection?: UseSingleSelectFilter<T>['selection'];
    /** custom props for the Select field simple component */
    selectTextFieldProps: Partial<SingleSelectTextFieldProps<T>> &
        Pick<SingleSelectTextFieldProps<T>, 'autoFilter' | 'getOptionId'>;
    /** callback when the select is open */
    onOpen?: () => void;
    /** default values */
    defaults?: Partial<UseSingleSelectFilterOptions<T>>;
}

const CLASSNAME = 'lumx-single-select-filter';

/**
 * Filter that renders the provided options as a list with a single option.
 * @family Filters
 * @param SingleSelectFilterProps
 * @returns SingleSelectFilter
 */
export const SingleSelectFilter = <T,>({
    options,
    onSelected,
    selection,
    selectTextFieldProps,
    mode,
    shouldDisplayOptionsDirectly,
    onOpen,
    className,
    ...props
}: SingleSelectFilterProps<T>) => {
    const filterUIProps = useFilterUIProps({
        parentClassName: CLASSNAME,
        className,
    });

    const isExposedFacet = mode === FILTERS_MODE.EXPOSED_FACETS;

    return (
        <Filter {...props} hideTitle mode={mode} shouldDisplayOptionsDirectly={shouldDisplayOptionsDirectly}>
            <SelectTextField<T>
                selectionType="single"
                label={isExposedFacet || props.hideTitle ? undefined : props.title}
                aria-label={isExposedFacet || props.hideTitle ? `${props.title}` : undefined}
                options={options}
                onChange={onSelected}
                value={selection}
                className={filterUIProps.className}
                onOpen={onOpen}
                {...selectTextFieldProps}
                labelProps={{
                    ...selectTextFieldProps.labelProps,
                    typography: filterUIProps.typography,
                }}
            />
        </Filter>
    );
};

Object.assign(SingleSelectFilter, { hook: useSingleSelectFilter });
